// src/context/NavigationContext.jsx
import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export const NavigationContext = createContext();

export const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { recordingId } = useParams();
  // Helper to extract recordingId from a level's route (e.g., /analysis/9/parameter-detail)
  const extractRecordingId = (route) => {
    const match = route && route.match(/\/analysis\/(\d+)/);
    return match ? match[1] : undefined;
  };
  const getReportLevel = (recId) => ({
    key: 'report',
    label: 'Sleep Report',
    route: recId ? `/analysis/${recId}/report` : '/analysis'
  });
  const [levels, setLevels] = useState([getReportLevel(recordingId)]);
  
  // Use a ref to track if we're handling a URL update
  const isHandlingUrl = useRef(false);

  // Helper function to safely encode/decode route history
  const encodeRouteHistory = (levels) => {
    return btoa(encodeURIComponent(JSON.stringify(levels)));
  };

  const decodeRouteHistory = (encoded) => {
    try {
      return JSON.parse(decodeURIComponent(atob(encoded)));
    } catch (e) {
      console.error('Error decoding route history:', e);
      return null;
    }
  };

  // Parse levels from URL on mount and route changes
  useEffect(() => {
    if (isHandlingUrl.current) {
      return; // Skip if we're handling a URL update
    }

    const params = new URLSearchParams(location.search);
    const routeHistory = params.get('history');
    
    if (routeHistory) {
      const parsedLevels = decodeRouteHistory(routeHistory);
      if (parsedLevels) {
        // Validate that higher levels always come first
        const isValidHierarchy = parsedLevels.every((level, index) => {
          if (index === 0) return level.key === 'report';
          const prevLevel = parsedLevels[index - 1];
          return isValidTransition(prevLevel.key, level.key);
        });

        if (isValidHierarchy) {
          setLevels(parsedLevels);
        } else {
          // If invalid hierarchy, reset to report
          // Try to extract recordingId from the previous level if possible
          let recId = undefined;
          if (parsedLevels && parsedLevels[1] && parsedLevels[1].route) {
            recId = extractRecordingId(parsedLevels[1].route);
          } else if (recordingId) {
            recId = recordingId;
          }
          setLevels([getReportLevel(recId)]);
        }
      }
    }
  }, [location.search]);

  // Helper function to check if a transition between levels is valid
  const isValidTransition = (fromKey, toKey) => {
    const validTransitions = {
      'report': ['parameterDetail', 'hypnogram'],
      'parameterDetail': ['hypnogram', 'epoch'],
      'hypnogram': ['epoch'],
      'epoch': ['micro-annotation', 'new-annotation'],
      'micro-annotation': [],
      'new-annotation': []
    };
    return validTransitions[fromKey]?.includes(toKey) ?? false;
  };

  // Helper function to update URL with current levels
  const updateUrl = useCallback((newLevels) => {
    isHandlingUrl.current = true;
    const params = new URLSearchParams();
    
    // Get the last level and its route
    const lastLevel = newLevels[newLevels.length - 1];
    const [pathname, existingSearch] = lastLevel.route.split('?');
    
    // Add the route history using safe encoding
    params.set('history', encodeRouteHistory(newLevels));
    
    // Add any query parameters from the route itself
    if (existingSearch) {
      const routeParams = new URLSearchParams(existingSearch);
      for (const [key, value] of routeParams) {
        if (key !== 'history') {  // Don't duplicate history
          params.set(key, value);
        }
      }
    }

    // Use setTimeout to ensure this happens after the current render
    setTimeout(() => {
      navigate({
        pathname: pathname,
        search: params.toString()
      });
      isHandlingUrl.current = false;
    }, 0);
  }, [navigate]);

  const openLevel = useCallback((newLevel) => {
    setLevels((prev) => {
      const lastLevel = prev[prev.length - 1];
      if (!isValidTransition(lastLevel.key, newLevel.key)) {
        console.warn('Invalid level transition:', lastLevel.key, 'to', newLevel.key);
        return prev;
      }
      const newLevels = [...prev, newLevel];
      updateUrl(newLevels);
      return newLevels;
    });
  }, [updateUrl]);

  const goToLevel = useCallback((levelKey) => {
    setLevels((prev) => {
      const index = prev.findIndex((level) => level.key === levelKey);
      if (index < 0) return prev;
      let newLevels = prev.slice(0, index + 1);
      // Always update the report level to use the recordingId from the next level if available
      if (levelKey === 'report') {
        let recId = undefined;
        if (prev[1] && prev[1].route) {
          recId = extractRecordingId(prev[1].route);
        } else if (prev[0] && prev[0].route) {
          recId = extractRecordingId(prev[0].route);
        } else if (recordingId) {
          recId = recordingId;
        }
        newLevels[0] = getReportLevel(recId);
      }
      updateUrl(newLevels);
      return newLevels;
    });
  }, [updateUrl, recordingId]);

  const closeLevel = useCallback((levelKey) => {
    setLevels((prev) => {
      const index = prev.findIndex((level) => level.key === levelKey);
      if (index <= 0) return prev; // Do not close root
      const newLevels = prev.slice(0, index);
      updateUrl(newLevels);
      return newLevels;
    });
  }, [updateUrl]);

  const updateLevel = useCallback((levelKey, newData) => {
    setLevels((prev) => {
      const newLevels = prev.map((level) => {
        if (level.key === levelKey) {
          if (level.label === newData.label && level.route === newData.route) {
            return level;
          }
          return { ...level, ...newData };
        }
        return level;
      });
      updateUrl(newLevels);
      return newLevels;
    });
  }, [updateUrl]);

  return (
    <NavigationContext.Provider
      value={{ levels, openLevel, goToLevel, closeLevel, updateLevel }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

// Replace static route with dynamic one using recordingId
export function getSleepReportRoute(recordingId) {
  return recordingId ? `/analysis/${recordingId}/report` : '/analysis';
}

// In your menu rendering logic, use getAnalysisLevels(recordingId) to generate the levels array
// Example usage:
// const levels = getAnalysisLevels(recordingId);
// ...existing code...
